import { useState, useEffect } from 'react'
import { message } from 'antd'
import { createInvoice, updateInvoice } from '../../../utils'
import { readBillingRecords, goodXSubmit, getPlanSubOptions, goodXResponse } from '../../../../Bookings/utils/utils'
import { readPatients } from '../../../../Admin/patients/utils/utils'
import { readBookings } from '../../../../Bookings/utils/utils'
import { readClinics } from '../../../../Admin/clinics/utils/utils'
import { findAgiliteUser } from '../../../../Auth/utils/utils'
import { handleError } from '../../../../lib/utils'
import dayjs from 'dayjs'
import ObjectID from 'bson-objectid'
import { useSelector } from 'react-redux'
import {
  logAuditEvent,
  AuditActions,
  AuditEntityTypes
} from '../../../../Billing-Module-Enhanced/backend-integration/api/auditTrail'

/**
 * Custom hook for managing invoice form operations
 */
export const useInvoiceForm = (initialData, isNew = false, onSuccess) => {
  const [loading, setLoading] = useState(false)
  const [formLoading, setFormLoading] = useState(true)
  const [invoiceData, setInvoiceData] = useState(initialData || {})
  const [items, setItems] = useState([])
  const [bookingRef, setBookingRef] = useState('')
  const [resubmitEnabled, setResubmitEnabled] = useState(false)
  const [saveForLaterEnabled, setSaveForLaterEnabled] = useState(false)
  const agiliteUser = useSelector((state) => state.auth.agiliteUser)
  const [goodXResponseData, setGoodXResponseData] = useState(null)

  // Determine if resubmit should be enabled based on claim status
  const claimHeaderStatusCode =
    goodXResponseData?.ProviderClaimResponse?.CommonProviderClaimResponseInfo[0]?.ClaimHeaderStatusCode[0]

  useEffect(() => {
    switch (claimHeaderStatusCode) {
      case 'swREJ':
      case 'mfREJ':
        setResubmitEnabled(true)
        break
      default:
        setResubmitEnabled(false)
        break
    }
  }, [claimHeaderStatusCode])

  // Initialize data
  useEffect(() => {
    if (!isNew) {
      loadExistingInvoice()
    } else {
      setBookingRef(initialData.bookingRef || new ObjectID().toString())
      setSaveForLaterEnabled(true)
      setFormLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load billing records when booking reference changes
  useEffect(() => {
    if (bookingRef) {
      fetchBillingRecords()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingRef])

  /**
   * Fetches billing records for the current booking
   */
  const fetchBillingRecords = async () => {
    setLoading(true)
    try {
      const tmpItems = await readBillingRecords({ bookingRef })
      setItems(tmpItems)
    } catch (e) {
      message.error(handleError(e, true))
    }
    setLoading(false)
  }

  /**
   * Loads data for an existing invoice
   */
  const loadExistingInvoice = async () => {
    let patientRecord = null
    let bookingRecord = null
    let medicalProfRecord = null
    let refMedProfRecord = null
    let assMedProfRecord = null
    let clinicRecord = null

    setFormLoading(true)

    try {
      await Promise.all([
        (async () => {
          const tmpData = await readPatients({ _id: initialData.userRef })
          patientRecord = tmpData.length > 0 ? tmpData[0] : null
        })(),
        (async () => {
          if (initialData.medicalProfRef) {
            medicalProfRecord = await findAgiliteUser(
              { _id: initialData.medicalProfRef },
              undefined,
              'firstName lastName'
            )
          }
        })(),
        (async () => {
          if (initialData.refMedProfRef) {
            refMedProfRecord = await findAgiliteUser(
              { _id: initialData.refMedProfRef },
              undefined,
              'firstName lastName'
            )
          }
        })(),
        (async () => {
          if (initialData.assMedProfRef) {
            assMedProfRecord = await findAgiliteUser(
              { _id: initialData.assMedProfRef },
              undefined,
              'firstName lastName'
            )
          }
        })(),
        (async () => {
          const tmpData = await readClinics({ _id: initialData.clinicRef })
          clinicRecord = tmpData.length > 0 ? tmpData[0] : null
        })(),
        (async () => {
          const tmpData = await readBookings({ _id: initialData.bookingRef })
          bookingRecord = tmpData.length > 0 ? tmpData[0] : null
          setBookingRef(initialData.bookingRef)
        })(),
        (async () => {
          if (initialData.goodXTicketNo) {
            const tmpData = await goodXResponse(initialData.goodXTicketNo)
            setGoodXResponseData(tmpData)
          }
        })()
      ])

      const tmpInvoiceData = {
        ...initialData,
        patientRecord,
        bookingRecord,
        medicalProfRecord,
        refMedProfRecord,
        assMedProfRecord,
        clinicRecord
      }

      if (tmpInvoiceData.status === 'draft') {
        setSaveForLaterEnabled(true)
      }

      setInvoiceData(tmpInvoiceData)
    } catch (e) {
      message.error(handleError(e, true))
    } finally {
      setFormLoading(false)
    }
  }

  /**
   * Updates invoice fields
   */
  const updateInvoiceFields = (changedFields, form) => {
    try {
      setInvoiceData({ ...invoiceData, ...changedFields })
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  /**
   * Calculate total amount from items array
   */
  const calculateTotalAmount = (items) => {
    if (!items || !Array.isArray(items)) return 0
    return items.reduce((total, item) => {
      const amount = parseFloat(item.amount || item.total || 0)
      return total + amount
    }, 0)
  }

  /**
   * Submits invoice to medical aid
   */
  const submitInvoice = async (formValues) => {
    try {
      let goodXTicketNo = ''
      let planSubOption = null
      let planSubOptionsData = null

      let patient = null
      let mainMember = null
      let medicalProf = null
      let clinic = null

      setLoading(true)
      setFormLoading(true)

      patient = invoiceData.patientRecord
      medicalProf = invoiceData.medicalProfRecord
      clinic = invoiceData.clinicRecord

      if (patient.mainMemberId) {
        mainMember = await findAgiliteUser({ _id: patient.mainMemberId })
      } else {
        mainMember = patient
      }

      // Determine claim type
      const claimType = formValues.claimType || 'medical_aid'

      // Process medical aid claim
      if (
        claimType === 'medical_aid' &&
        formValues.invoiceType === 'medical' &&
        mainMember.medicalAid?.name &&
        patient.medicalAid?.name &&
        clinic.billingProfile?.switchingEnabled
      ) {
        planSubOptionsData = await getPlanSubOptions(mainMember.medicalAid.planOptionCode)

        if (planSubOptionsData.planSubOptions.pageResult.length > 0) {
          const tmpIndex = planSubOptionsData.planSubOptions.pageResult.findIndex(
            (option) => option.option === 'Acute Meds'
          )

          if (tmpIndex !== -1) {
            planSubOption = planSubOptionsData.planSubOptions.pageResult[tmpIndex]
          } else {
            planSubOption = planSubOptionsData.planSubOptions.pageResult[0]
          }
        }

        goodXTicketNo = await goodXSubmit({
          patientRecord: patient,
          mainMemberRecord: mainMember,
          clinicRecord: clinic,
          billingItems: items.filter((item) => item.code && item.name && item.type),
          medicalProf,
          planSubOption,
          bookingDate: formValues.bookingDate
        })
      }
      // Process WCA (Workman's Compensation Act) claim
      else if (claimType === 'WCA' || formValues.invoiceType === 'WCA') {
        try {
          // Import WCA claims utility
          const { submitWCAClaim, isWCAPatient } = await import('../../../utils/wca-claims')

          // Verify this is actually a WCA patient
          if (!isWCAPatient(patient)) {
            console.warn('⚠️ [INVOICE-FORM] Patient does not have WCA scheme but WCA claim type selected')
          }

          // Prepare invoice data with WCA fields
          const wcaInvoiceData = {
            ...formValues,
            wcaClaimNumber: formValues.wcaClaimNumber,
            wcaInjuryDate: formValues.wcaInjuryDate,
            wcaEmployer: formValues.wcaEmployer,
            wcaEmployerRegNumber: formValues.wcaEmployerRegNumber,
            wcaOccupation: formValues.wcaOccupation,
            wcaInjuryDescription: formValues.wcaInjuryDescription,
            wcaTreatmentDescription: formValues.wcaTreatmentDescription,
            total: calculateTotalAmount(items),
            items: items.filter((item) => item.code && item.name && item.type)
          }

          console.log('🏥 [INVOICE-FORM] Processing WCA claim')
          console.log('🏥 [INVOICE-FORM] WCA invoice data:', wcaInvoiceData)
          console.log('🏥 [INVOICE-FORM] Patient data:', patient)

          // Submit WCA claim to GoodX with debtor_type: "B"
          const wcaResult = await submitWCAClaim(wcaInvoiceData, patient)

          console.log('🏥 [INVOICE-FORM] WCA claim result:', wcaResult)

          if (wcaResult.success) {
            goodXTicketNo = wcaResult.ticketNo || wcaResult.claimNumber
            console.log(`✅ [INVOICE-FORM] WCA claim submitted successfully with ticket: ${goodXTicketNo}`)
          } else {
            console.warn('⚠️ [INVOICE-FORM] WCA claim submission failed:', wcaResult.error)
            // Don't fail the entire invoice creation if GoodX submission fails
          }
        } catch (wcaError) {
          console.error('❌ [INVOICE-FORM] Failed to submit WCA claim to GoodX:', {
            error: wcaError.message,
            stack: wcaError.stack,
            response: wcaError.response?.data
          })
          // Don't fail the entire invoice creation if GoodX submission fails
        }
      }
      // Process legacy compensation fund claims (COID, FEM, SAPS, RMA) - DEPRECATED
      else if (['COID', 'FEM', 'SAPS', 'RMA'].includes(claimType)) {
        console.warn(
          '⚠️ [INVOICE-FORM] Legacy compensation claim types (COID, FEM, SAPS, RMA) are deprecated. Use WCA instead.'
        )

        try {
          // Import WCA claims utility instead
          const { submitWCAClaim } = await import('../../../utils/wca-claims')

          // Convert legacy fields to WCA format
          const wcaInvoiceData = {
            ...formValues,
            wcaClaimNumber: formValues.iod_claimnumber || formValues.claimNumber,
            wcaInjuryDate: formValues.iod_injury_date || formValues.injuryDate,
            wcaEmployer: formValues.employerName || 'Unknown Employer',
            wcaEmployerRegNumber: formValues.employerReference || 'N/A',
            wcaOccupation: formValues.occupation || '',
            wcaInjuryDescription: formValues.iod_injury_description || formValues.injuryDescription,
            wcaTreatmentDescription: formValues.treatmentType || formValues.treatmentDescription,
            total: calculateTotalAmount(items),
            items: items.filter((item) => item.code && item.name && item.type)
          }

          console.log(`🏥 [INVOICE-FORM] Converting legacy ${claimType} claim to WCA format`)
          console.log('🏥 [INVOICE-FORM] WCA invoice data:', wcaInvoiceData)

          // Submit as WCA claim
          const wcaResult = await submitWCAClaim(wcaInvoiceData, patient)

          if (wcaResult.success) {
            goodXTicketNo = wcaResult.ticketNo || wcaResult.claimNumber
            console.log(
              `✅ [INVOICE-FORM] Legacy ${claimType} claim converted and submitted as WCA with ticket: ${goodXTicketNo}`
            )
          } else {
            console.warn(`⚠️ [INVOICE-FORM] Legacy ${claimType} to WCA conversion failed:`, wcaResult.error)
          }
        } catch (legacyError) {
          console.error(`❌ [INVOICE-FORM] Failed to convert legacy ${claimType} claim to WCA:`, {
            error: legacyError.message,
            stack: legacyError.stack,
            response: legacyError.response?.data
          })
        }
      }

      // Create new invoice or update existing one
      if (isNew) {
        // For new invoices, always derive billingId from the selected patient/main member
        const newBillingId = patient?.billingId || mainMember?.billingId
        if (!newBillingId) {
          throw new Error('Cannot create invoice - patient does not have a billing account. Please contact support.')
        }

        // Prepare WCA specific fields if applicable
        const wcaFields = {}
        const claimType = formValues.claimType || 'medical_aid'

        if (claimType === 'WCA' || formValues.invoiceType === 'WCA') {
          // Include WCA-specific fields
          if (formValues.wcaClaimNumber) wcaFields.wcaClaimNumber = formValues.wcaClaimNumber
          if (formValues.wcaInjuryDate) wcaFields.wcaInjuryDate = formValues.wcaInjuryDate
          if (formValues.wcaEmployer) wcaFields.wcaEmployer = formValues.wcaEmployer
          if (formValues.wcaEmployerRegNumber) wcaFields.wcaEmployerRegNumber = formValues.wcaEmployerRegNumber
          if (formValues.wcaOccupation) wcaFields.wcaOccupation = formValues.wcaOccupation
          if (formValues.wcaInjuryDescription) wcaFields.wcaInjuryDescription = formValues.wcaInjuryDescription
          if (formValues.wcaTreatmentDescription) wcaFields.wcaTreatmentDescription = formValues.wcaTreatmentDescription
        }
        // Legacy compensation fields (deprecated but maintained for backward compatibility)
        else if (['COID', 'FEM', 'SAPS', 'RMA'].includes(claimType)) {
          // Convert legacy fields to WCA format for storage
          if (formValues.iod_claimnumber) wcaFields.wcaClaimNumber = formValues.iod_claimnumber
          if (formValues.iod_injury_date) wcaFields.wcaInjuryDate = formValues.iod_injury_date
          if (formValues.iod_injury_description) wcaFields.wcaInjuryDescription = formValues.iod_injury_description
          if (formValues.employerName) wcaFields.wcaEmployer = formValues.employerName
          if (formValues.employerReference) wcaFields.wcaEmployerRegNumber = formValues.employerReference
          if (formValues.treatmentType) wcaFields.wcaTreatmentDescription = formValues.treatmentType
          // Keep legacy fields for backward compatibility
          if (formValues.iod_claimnumber) wcaFields.iod_claimnumber = formValues.iod_claimnumber
          if (formValues.iod_injury_date) wcaFields.iod_injury_date = formValues.iod_injury_date
          if (formValues.iod_injury_description) wcaFields.iod_injury_description = formValues.iod_injury_description
          if (formValues.employerName) wcaFields.employerName = formValues.employerName
          if (formValues.employerReference) wcaFields.employerReference = formValues.employerReference
          if (formValues.caseNumber) wcaFields.caseNumber = formValues.caseNumber
          if (formValues.accidentLocation) wcaFields.accidentLocation = formValues.accidentLocation
          if (formValues.treatmentType) wcaFields.treatmentType = formValues.treatmentType
          // Mark as converted from legacy
          wcaFields.convertedFromLegacy = claimType
        }

        await createInvoice({
          userRef: formValues.userRef,
          billingId: newBillingId,
          bookingRef: bookingRef,
          medicalProfRef: formValues.medicalProfRef,
          clinicRef: formValues.clinicRef,
          // Always use the invoiceType from the form, don't default to 'private'
          invoiceType: formValues.invoiceType,
          // Include claimType for WCA and legacy compensation claims
          claimType: claimType,
          // Include WCA-specific fields
          ...wcaFields,
          bookingDate: dayjs(formValues.bookingDate).format('YYYY-MM-DD'),
          status: '',
          invoiceNo: dayjs(new Date()).format('YYMMDDHHmmss'),
          createdAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
          createdBy: agiliteUser._id,
          modifiedAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
          modifiedBy: agiliteUser._id,
          goodXTicketNo
        })
      } else {
        await updateInvoice(invoiceData._id, {
          userRef: invoiceData.patientRecord._id,
          bookingRef: bookingRef,
          medicalProfRef: invoiceData.medicalProfRecord._id,
          clinicRef: invoiceData.clinicRecord._id,
          // Always use the invoiceType from the form
          invoiceType: formValues.invoiceType,
          // Include claimType for compensation fund claims
          claimType: formValues.claimType || invoiceData.claimType || 'medical_aid',
          bookingDate: invoiceData.bookingDate,
          status: '',
          invoiceNo: dayjs(new Date()).format('YYMMDDHHmmss'),
          modifiedAt: dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'),
          modifiedBy: agiliteUser._id,
          goodXTicketNo
        })
      }

      message.success(`Invoice successfully ${isNew || saveForLaterEnabled ? 'created' : 'resubmitted'}`)

      if (onSuccess) {
        onSuccess()
      }

      return true
    } catch (e) {
      message.error(handleError(e, true))
      return false
    } finally {
      setLoading(false)
      setFormLoading(false)
    }
  }

  /**
   * Saves invoice as a draft
   */
  const saveForLater = async (formValues) => {
    setLoading(true)
    try {
      // Ensure we have complete patient information for the invoice list display
      const patientRecord = formValues.patientRecord || invoiceData.patientRecord

      // Get billingId from patient record (check root level)
      const billingId = patientRecord?.billingId
      if (!billingId) {
        throw new Error('Cannot save invoice - patient does not have a billing account. Please contact support.')
      }

      const data = {
        userRef: formValues.userRef,
        billingId: billingId,
        bookingRef: bookingRef,
        medicalProfRef: formValues.medicalProfRef,
        clinicRef: formValues.clinicRef,
        invoiceType: formValues.invoiceType,
        status: 'draft',
        invoiceNo: isNew ? dayjs(new Date()).format('YYMMDDHHmmss') : invoiceData.invoiceNo,
        bookingDate: dayjs(formValues.bookingDate).format('YYYY-MM-DD'),
        // Store calculated display values directly in the invoice document
        fullName: patientRecord ? `${patientRecord.firstName} ${patientRecord.lastName}` : '',
        idNo: patientRecord?.idNo || ''
      }

      if (isNew) {
        data.createdAt = dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss')
        data.modifiedAt = dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss')
        data.createdBy = agiliteUser._id
        data.modifiedBy = agiliteUser._id
        const result = await createInvoice(data)

        // Log audit trail for invoice creation
        await logAuditEvent(
          AuditActions.CREATE,
          AuditEntityTypes.INVOICE,
          result._id || data.invoiceNo,
          `Invoice draft ${data.invoiceNo} created`,
          {
            invoiceNo: data.invoiceNo,
            patientId: data.userRef,
            billingId: data.billingId,
            status: 'draft',
            invoiceType: data.invoiceType
          }
        )
      } else {
        data.modifiedAt = dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss')
        data.modifiedBy = agiliteUser._id
        await updateInvoice(invoiceData._id, data)

        // Log audit trail for invoice update
        await logAuditEvent(
          AuditActions.UPDATE,
          AuditEntityTypes.INVOICE,
          invoiceData._id,
          `Invoice draft ${data.invoiceNo} updated`,
          {
            invoiceNo: data.invoiceNo,
            patientId: data.userRef,
            billingId: data.billingId,
            status: 'draft',
            action: 'draft_save'
          }
        )
      }

      message.success('Invoice successfully saved for later')

      if (onSuccess) {
        onSuccess()
      }

      return true
    } catch (e) {
      message.error(handleError(e, true))
      return false
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    formLoading,
    invoiceData,
    items,
    bookingRef,
    resubmitEnabled,
    saveForLaterEnabled,
    setItems,
    updateInvoiceFields,
    fetchBillingRecords,
    submitInvoice,
    saveForLater
  }
}
